import { useState } from 'react';
import { Text, Checkbox, FormControl, FormHelperText, FormLabel } from '@chakra-ui/react';

import { TBTest } from 'types';
import { ModalColumns } from '../../../atoms/modal';
import { Link } from '../../../atoms/link';
import { RadioOptionsWithConfirm } from '../../../formInputs/RadioOptions';
import UploadActions from '../../../atoms/upload-actions';
import UploadExample from '../../../atoms/upload-example';
import UploadButtonSection from '../../../atoms/upload-button-section';
import UploadCompleteSection from '../../../molecules/uploadComplete';
import { IQuestion, ITBTestMetaData } from '../../../../types';
import AcknowledgeBox from '../../../molecules/acknowledgeBox';
import placeholderExample from '../../../../assets/images/sample-tb-test-certificate.jpg';
import { RequirementsRemedy } from '../../../atoms/RequirementsRemedy';

export interface IProps {
  docKey: string;
  complete: boolean;
  fileNames: string[];
  document?: TBTest;
  onClose: () => void;
  onContinue: () => void;
  onUpload: (
    sponsorType: string | null,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    modalTitle: string,
    fileNames: string[],
    metaData: ITBTestMetaData
  ) => void;
  onTBTestMetaUpdate: (metaData: ITBTestMetaData) => void;
  infoOnly: boolean;
  docStatus: string;
  onAcknowledgeToggle: (status: string) => void;
}

const TBTestInnerModal: React.FC<IProps> = (props: IProps) => {
  const {
    isTBTestPending = false,
    isReturningUKResident,
    awayFromUKMoreThan2Yrs,
    hasLivedInExemptCountry,
    hasStayedInExemptCountry
  } = props.document || {};

  const { docKey, infoOnly, docStatus } = props;

  const [hasAppliedForTBTest, setHasAppliedForTBTest] = useState(isTBTestPending);

  const [returningResident, setReturningResident] = useState(
    isReturningUKResident === undefined ? '' : isReturningUKResident ? 'Yes' : 'No'
  );
  const [hasBeenAwayfromUKMoreThan2Yrs, setHasBeenAwayfromUKMoreThan2Yrs] = useState(
    awayFromUKMoreThan2Yrs === undefined ? '' : awayFromUKMoreThan2Yrs ? 'Yes' : 'No'
  );
  const [livedInExemptCountry, setLivedInExemptCountry] = useState(
    hasLivedInExemptCountry === undefined ? '' : hasLivedInExemptCountry ? 'Yes' : 'No'
  );
  const [awayFromExemptCountry6Mnths, setAwayFromExemptCountry6Mnths] = useState(
    hasStayedInExemptCountry === undefined ? '' : hasStayedInExemptCountry ? 'Yes' : 'No'
  );

  const handleUpload = () => {
    props.onUpload(null, null, 'tbTest', docKey, 'Tuberculosis Test Results', props.fileNames, {
      isTBTestPending: hasAppliedForTBTest,
      isReturningUKResident: returningResident === '' ? undefined : returningResident === 'Yes',
      awayFromUKMoreThan2Yrs:
        hasBeenAwayfromUKMoreThan2Yrs === '' ? undefined : hasBeenAwayfromUKMoreThan2Yrs === 'Yes',
      hasLivedInExemptCountry: livedInExemptCountry === '' ? undefined : livedInExemptCountry === 'Yes',
      hasStayedInExemptCountry: awayFromExemptCountry6Mnths === '' ? undefined : awayFromExemptCountry6Mnths === 'Yes'
    });
  };

  const updateTBTestMeta = () => {
    props.onTBTestMetaUpdate({
      isTBTestPending: hasAppliedForTBTest,
      isReturningUKResident: returningResident === '' ? undefined : returningResident === 'Yes',
      awayFromUKMoreThan2Yrs:
        hasBeenAwayfromUKMoreThan2Yrs === '' ? undefined : hasBeenAwayfromUKMoreThan2Yrs === 'Yes',
      hasLivedInExemptCountry: livedInExemptCountry === '' ? undefined : livedInExemptCountry === 'Yes',
      hasStayedInExemptCountry: awayFromExemptCountry6Mnths === '' ? undefined : awayFromExemptCountry6Mnths === 'Yes'
    });
  };

  const handleQuestionKeyDown = (event: any, question: any, propSetter: (val: string) => void) => {
    switch (event.key.toLowerCase()) {
      case 'y':
        propSetter('Yes');
        break;
      case 'n':
        propSetter('No');
        break;
      case 'enter':
      case 'space':
        propSetter(question.label);
        break;
    }
  };

  const isTBTestRequired = () => {
    return returningResident !== '' && livedInExemptCountry !== '' && !isTBTestNotRequired();
  };

  const isTBTestNotRequired = () => {
    return (
      (returningResident !== '' &&
        livedInExemptCountry !== '' &&
        returningResident === 'Yes' &&
        hasBeenAwayfromUKMoreThan2Yrs === 'No') ||
      (livedInExemptCountry === 'Yes' && awayFromExemptCountry6Mnths === 'No')
    );
  };

  const onContinue = () => {
    if (
      isTBTestPending !== hasAppliedForTBTest ||
      isReturningUKResident !== (returningResident === 'Yes') ||
      awayFromUKMoreThan2Yrs !== (hasBeenAwayfromUKMoreThan2Yrs === 'Yes') ||
      hasLivedInExemptCountry !== (livedInExemptCountry === 'Yes') ||
      hasStayedInExemptCountry !== (awayFromExemptCountry6Mnths === 'Yes')
    ) {
      updateTBTestMeta();
    }
    props.onClose();
  };

  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      props.onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    props.onClose();
  };

  const toggleAcknowledge = (_key: string, value: boolean) => {
    // prevent call to API if previous call not yet complete
    if ((value === true && docStatus !== 'ACKNOWLEDGED') || (value === false && docStatus === 'ACKNOWLEDGED')) {
      props.onAcknowledgeToggle(value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED');
    }
  };

  const TBAlert = (props: { uploadRequired?: boolean }) => {
    const { uploadRequired } = props;

    return (
      <>
        <Text>
          All international students from your country are required to take a tuberculosis test. You will need to
          arrange this test with your doctor
          {uploadRequired ? ' and upload your results here when you receive them' : '.'}
        </Text>

        <Text>You can read more about the requirements at the link below:</Text>

        <Text>
          <Link href="https://www.gov.uk/tb-test-visa" isExternal>
            Tuberculosis tests for visa applicants
          </Link>
        </Text>

        {!uploadRequired ? (
          <Text>You may be asked for a copy of these test results as part of your visa application.</Text>
        ) : null}
      </>
    );
  };

  return (
    <>
      {infoOnly ? (
        <>
          <TBAlert />

          <AcknowledgeBox
            id={docKey}
            blurb="Please confirm you have read and understand the above."
            isTicked={docStatus === 'ACKNOWLEDGED'}
            onChange={toggleAcknowledge}
          />

          <UploadActions
            complete={docStatus === 'ACKNOWLEDGED'}
            onContinue={handleOnClose}
            label="Return to document upload"
          />
        </>
      ) : (
        <>
          {props.complete ? (
            <UploadCompleteSection fileNames={props.fileNames} onUpload={handleUpload} onContinue={props.onClose} />
          ) : (
            <>
              <TBAlert uploadRequired />

              <RadioOptionsWithConfirm
                id={'uk-resident'}
                label="Are you a returning UK resident?"
                isInline
                questions={[
                  {
                    char: 'Y',
                    label: 'Yes',
                    selected: returningResident === 'Yes'
                  },
                  {
                    char: 'N',
                    label: 'No',
                    selected: returningResident === 'No'
                  }
                ]}
                onClick={(question: IQuestion) => setReturningResident(question.label)}
                onKeyDown={(evt: any, question: IQuestion) =>
                  handleQuestionKeyDown(evt, question, setReturningResident)
                }
              />

              {returningResident === 'Yes' ? (
                <RadioOptionsWithConfirm
                  id={'uk-resident-away-2-years'}
                  label="Have you been away from the UK for more than 2 years?"
                  isInline
                  questions={[
                    {
                      char: 'Y',
                      label: 'Yes',
                      selected: hasBeenAwayfromUKMoreThan2Yrs === 'Yes'
                    },
                    {
                      char: 'N',
                      label: 'No',
                      selected: hasBeenAwayfromUKMoreThan2Yrs === 'No'
                    }
                  ]}
                  onClick={(question: IQuestion) => setHasBeenAwayfromUKMoreThan2Yrs(question.label)}
                />
              ) : null}

              <RadioOptionsWithConfirm
                id={'tb-exempt-country'}
                label="Have you lived for at least 6 months in a
                    country where TB screening is not required
                    by the UK? (see list above)"
                isInline
                questions={[
                  {
                    char: 'Y',
                    label: 'Yes',
                    selected: livedInExemptCountry === 'Yes'
                  },
                  {
                    char: 'N',
                    label: 'No',
                    selected: livedInExemptCountry === 'No'
                  }
                ]}
                onClick={(question: IQuestion) => setLivedInExemptCountry(question.label)}
              />

              {livedInExemptCountry === 'Yes' ? (
                <RadioOptionsWithConfirm
                  id={'away-from-tb-exempt-country-6mnths'}
                  label="Have you been away from that country for more than 6 months?"
                  isInline
                  questions={[
                    {
                      char: 'Y',
                      label: 'Yes',
                      selected: awayFromExemptCountry6Mnths === 'Yes'
                    },
                    {
                      char: 'N',
                      label: 'No',
                      selected: awayFromExemptCountry6Mnths === 'No'
                    }
                  ]}
                  onClick={(question: IQuestion) => setAwayFromExemptCountry6Mnths(question.label)}
                />
              ) : null}

              {isTBTestNotRequired() ? (
                <>
                  <RequirementsRemedy isSatisfied>
                    Your responses indicated you do not require a TB test. This item will be marked as Done. You may be
                    contacted for more information.
                  </RequirementsRemedy>
                </>
              ) : null}

              {isTBTestRequired() ? (
                <FormControl data-selector="form-field" mb={10}>
                  <FormLabel data-selector="label" fontSize="lg">
                    Have you applied for your TB Test?
                  </FormLabel>

                  <FormHelperText>Tick the box below after you apply</FormHelperText>

                  <Checkbox
                    data-selector={`checkbox-container`}
                    isChecked={hasAppliedForTBTest}
                    onChange={() => setHasAppliedForTBTest(!hasAppliedForTBTest)}
                    variant="outline"
                    size="lg"
                  >
                    I have applied
                  </Checkbox>
                </FormControl>
              ) : null}

              {isTBTestRequired() && hasAppliedForTBTest ? (
                <UploadButtonSection onUpload={handleUpload}>
                  Awaiting upload of <b>Tuberculosis Test Results</b>
                </UploadButtonSection>
              ) : null}

              <UploadActions complete={props.complete} onContinue={onContinue} />
            </>
          )}
        </>
      )}
    </>
  );
};

const TBTestModal: React.FC<IProps> = (props: IProps) => {
  const { docStatus } = props;
  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      props.onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    props.onClose();
  };

  return (
    <ModalColumns
      id={'tbTest'}
      title={'Tuberculosis Test Results'}
      onClose={handleOnClose}
      hideFooter
      supportingColumn={<UploadExample altText="Test certificate example" url={placeholderExample} />}
    >
      <TBTestInnerModal {...props} />
    </ModalColumns>
  );
};

export default TBTestModal;
